import { useEffect, useMemo, useState } from 'react';
import {
  addPeriods,
  BUSINESS_DAY_RULES,
  PAYMENT_FREQUENCIES,
  PAYMENT_FREQUENCY_LABELS,
  PERIODS_PER_YEAR,
  RATE_BASES,
  RATE_BASIS_LABELS,
  projectSchedule,
  summarizeTerms,
  type Account,
  type BusinessDayRule,
  type LoanTerms,
  type PaymentFrequency,
  type RateBasis,
} from '@accounting/shared';
import { AccountPicker } from '../accounts/AccountPicker';
import { ContactPicker } from '../contacts/ContactPicker';
import { formatAccountingDate, formatCurrency, todayIso } from '../../lib/formatters';

// One builder for both registers. The page that hosts it decides which side
// of the entry the principal account sits on and maps the values onto its own
// input type; everything the user sees here is the same for a note the company
// owes and a note it holds. Rules: docs/claude/loans.md.

export type LoanSide = 'payable' | 'receivable';

export interface LoanBuilderValues {
  name: string;
  contactId: string | null;
  principalAccountId: string;
  interestAccountId: string;
  bankAccountId: string | null;
  autoPostPayments: boolean;
  terms: LoanTerms & {
    lateChargePct?: string | null;
    lateChargeGraceDays?: number | null;
    defaultRateSpreadPct?: string | null;
  };
  funding?: {
    consolidate: { accountId: string; amount: string }[];
    advances: { accountId: string; amount: string }[];
  };
}

interface Props {
  side: LoanSide;
  companyId: string;
  accounts: Account[];
  onSubmit: (values: LoanBuilderValues) => void;
  isPending: boolean;
  errorMessage?: string | null;
  cancelHref: string;
}

interface FundingRow {
  key: number;
  accountId: string;
  amount: string;
}

const SIDE_TEXT = {
  payable: {
    contact: 'Lender',
    principalAccount: 'Liability account',
    principalHint: 'Where the balance owed lives (Notes Payable).',
    interestAccount: 'Interest expense',
    bankAccount: 'Pay from',
    consolidateTitle: 'Existing balances being rolled into this note',
    consolidateHint: 'Each prior liability is debited to zero and the note is credited for the total.',
    advancesTitle: 'New money received',
    advancesHint: 'The bank account the new advance landed in.',
    existingLabel: 'The balance is already in the liability account',
    postLabel: 'Consolidate existing balances and/or record new money',
    defaultPrincipalCode: '2700',
    defaultInterestCode: '8000',
    defaultBankCode: '1000',
  },
  receivable: {
    contact: 'Borrower',
    principalAccount: 'Receivable account',
    principalHint: 'The Notes/Loans Receivable asset that carries the balance.',
    interestAccount: 'Interest income',
    bankAccount: 'Receive into',
    consolidateTitle: 'Existing receivable balances being rolled into this note',
    consolidateHint: 'Each prior receivable is credited to zero and the note receivable is debited for the total.',
    advancesTitle: 'New money paid out',
    advancesHint: 'The bank account the advance was paid from.',
    existingLabel: 'The balance is already in the receivable account',
    postLabel: 'Consolidate existing balances and/or record new money',
    defaultPrincipalCode: '',
    defaultInterestCode: '7000',
    defaultBankCode: '1000',
  },
} as const;

const money = (v: string): string => String(parseFloat(v) || 0);
const cleanMoney = (v: string): string => v.replace(/[^0-9.]/g, '');

export function LoanBuilder({ side, companyId, accounts, onSubmit, isPending, errorMessage, cancelHref }: Props) {
  const text = SIDE_TEXT[side];
  const byCode = (code: string) => accounts.find((a) => a.code === code)?.id ?? '';

  const principalType = side === 'payable' ? 'liability' : 'asset';
  const isPrincipalSelectable = (a: Account) => a.isActive && a.allowDirectPosting && a.accountType === principalType;
  const isInterestSelectable = (a: Account) =>
    a.isActive &&
    a.allowDirectPosting &&
    (side === 'payable'
      ? a.accountType === 'expense' || a.accountType === 'other_expense'
      : a.accountType === 'revenue' || a.accountType === 'other_income');
  const isBankSelectable = (a: Account) => a.isActive && a.allowDirectPosting && a.accountType === 'asset';

  // ── Identity & accounts ──
  const [name, setName] = useState('');
  const [contactId, setContactId] = useState('');
  const [principalAccountId, setPrincipalAccountId] = useState('');
  const [interestAccountId, setInterestAccountId] = useState('');
  const [bankAccountId, setBankAccountId] = useState('');
  const [autoPost, setAutoPost] = useState(true);

  useEffect(() => {
    if (accounts.length === 0) return;
    if (text.defaultPrincipalCode) setPrincipalAccountId((v) => v || byCode(text.defaultPrincipalCode));
    setInterestAccountId((v) => v || byCode(text.defaultInterestCode));
    setBankAccountId((v) => v || byCode(text.defaultBankCode));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts.length]);

  // ── Terms ──
  const [principal, setPrincipal] = useState('');
  const [annualRatePct, setAnnualRatePct] = useState('6');
  const [rateBasis, setRateBasis] = useState<RateBasis>('nominal');
  const [frequency, setFrequency] = useState<PaymentFrequency>('monthly');
  const [amortizationPeriods, setAmortizationPeriods] = useState('60');
  const [numberOfPayments, setNumberOfPayments] = useState('');
  const [paymentOverride, setPaymentOverride] = useState('');
  const [originationDate, setOriginationDate] = useState(todayIso());
  const [firstPaymentDate, setFirstPaymentDate] = useState(() => addPeriods(todayIso(), 'monthly', 1));
  const [firstTouched, setFirstTouched] = useState(false);
  const [businessDayRule, setBusinessDayRule] = useState<BusinessDayRule>('next_business_day');
  const [lateChargePct, setLateChargePct] = useState('');
  const [graceDays, setGraceDays] = useState('');
  const [defaultSpread, setDefaultSpread] = useState('');

  // Keep the first payment one period after origination until the owner edits it.
  useEffect(() => {
    if (!firstTouched && originationDate) setFirstPaymentDate(addPeriods(originationDate, frequency, 1));
  }, [originationDate, frequency, firstTouched]);

  const ppy = PERIODS_PER_YEAR[frequency];
  const amortPeriodsNum = Math.max(0, Math.round(Number(amortizationPeriods) || 0));
  const amortYears = amortPeriodsNum > 0 ? +(amortPeriodsNum / ppy).toFixed(2) : 0;
  const paymentsNum = numberOfPayments.trim() ? Math.max(0, Math.round(Number(numberOfPayments) || 0)) : amortPeriodsNum;
  const paymentsYears = paymentsNum > 0 ? +(paymentsNum / ppy).toFixed(2) : 0;

  const setAmortYears = (years: string) => {
    const y = parseFloat(years);
    if (Number.isFinite(y) && y > 0) setAmortizationPeriods(String(Math.round(y * ppy)));
  };

  const terms: LoanTerms | null = useMemo(() => {
    const p = parseFloat(principal);
    if (!(p > 0) || amortPeriodsNum < 1 || paymentsNum < 1 || paymentsNum > amortPeriodsNum) return null;
    if (!originationDate || !firstPaymentDate || firstPaymentDate <= originationDate) return null;
    return {
      principal: money(principal),
      annualRatePct: String(parseFloat(annualRatePct) || 0),
      rateBasis,
      paymentFrequency: frequency,
      amortizationPeriods: amortPeriodsNum,
      numberOfPayments: paymentsNum,
      paymentAmount: paymentOverride.trim() ? money(paymentOverride) : undefined,
      originationDate,
      firstPaymentDate,
      businessDayRule,
    };
  }, [principal, annualRatePct, rateBasis, frequency, amortPeriodsNum, paymentsNum, paymentOverride, originationDate, firstPaymentDate, businessDayRule]);

  const summary = useMemo(() => (terms ? summarizeTerms(terms) : null), [terms]);
  const schedule = useMemo(() => (terms ? projectSchedule(terms) : []), [terms]);
  const [showAllRows, setShowAllRows] = useState(false);
  const visibleRows = showAllRows ? schedule : schedule.slice(0, 60);
  const balloonRow = schedule.find((r) => r.kind === 'balloon');

  // ── Funding ──
  const [fundingMode, setFundingMode] = useState<'existing' | 'post'>('existing');
  const [consolidate, setConsolidate] = useState<FundingRow[]>([]);
  const [advances, setAdvances] = useState<FundingRow[]>([]);
  const nextKey = useMemo(() => ({ v: 1 }), []);
  const addRow = (set: typeof setConsolidate) => set((rows) => [...rows, { key: nextKey.v++, accountId: '', amount: '' }]);
  const updateRow = (set: typeof setConsolidate, key: number, patch: Partial<FundingRow>) =>
    set((rows) => rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const removeRow = (set: typeof setConsolidate, key: number) => set((rows) => rows.filter((r) => r.key !== key));

  const fundingTotal = [...consolidate, ...advances].reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  const principalNum = parseFloat(principal) || 0;
  const fundingDiff = +(fundingTotal - principalNum).toFixed(2);
  const fundingRowsValid = [...consolidate, ...advances].every((r) => r.accountId && parseFloat(r.amount) > 0);
  const fundingOk =
    fundingMode === 'existing' || (consolidate.length + advances.length > 0 && fundingRowsValid && Math.abs(fundingDiff) < 0.005);

  const acctLabel = (id: string) => {
    const a = accounts.find((x) => x.id === id);
    return a ? `${a.code} ${a.name}` : '—';
  };

  const valid =
    name.trim() &&
    principalAccountId &&
    interestAccountId &&
    terms !== null &&
    summary !== null &&
    fundingOk &&
    (!autoPost || bankAccountId);

  const submit = () => {
    if (!terms || !valid) return;
    onSubmit({
      name: name.trim(),
      contactId: contactId || null,
      principalAccountId,
      interestAccountId,
      bankAccountId: bankAccountId || null,
      autoPostPayments: autoPost,
      terms: {
        ...terms,
        lateChargePct: lateChargePct.trim() ? String(parseFloat(lateChargePct) || 0) : null,
        lateChargeGraceDays: graceDays.trim() ? Math.max(0, Math.round(Number(graceDays) || 0)) : null,
        defaultRateSpreadPct: defaultSpread.trim() ? String(parseFloat(defaultSpread) || 0) : null,
      },
      funding:
        fundingMode === 'post'
          ? {
              consolidate: consolidate.map((r) => ({ accountId: r.accountId, amount: money(r.amount) })),
              advances: advances.map((r) => ({ accountId: r.accountId, amount: money(r.amount) })),
            }
          : undefined,
    });
  };

  const pick = (val: string, set: (v: string) => void, pred: (a: Account) => boolean, placeholder?: string) => (
    <div style={{ maxWidth: 360 }}>
      <AccountPicker accounts={accounts} value={val} onChange={set} predicate={pred} placeholder={placeholder} />
    </div>
  );

  const fundingTable = (
    title: string,
    hint: string,
    rows: FundingRow[],
    set: typeof setConsolidate,
    pred: (a: Account) => boolean,
  ) => (
    <div style={{ marginTop: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
        <strong style={{ fontSize: 13 }}>{title}</strong>
        <button type="button" className="btn-secondary" onClick={() => addRow(set)}>+ Add row</button>
      </div>
      <p className="muted" style={{ fontSize: 12, margin: '2px 0 6px' }}>{hint}</p>
      {rows.map((r) => (
        <div key={r.key} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 260px', minWidth: 220 }}>
            <AccountPicker accounts={accounts} value={r.accountId} onChange={(id) => updateRow(set, r.key, { accountId: id })} predicate={pred} placeholder="— account —" />
          </div>
          <input
            inputMode="decimal"
            value={r.amount}
            onChange={(e) => updateRow(set, r.key, { amount: cleanMoney(e.target.value) })}
            placeholder="0.00"
            style={{ width: 140, textAlign: 'right' }}
            aria-label="Amount"
          />
          <button type="button" className="btn-secondary" onClick={() => removeRow(set, r.key)} aria-label="Remove row">×</button>
        </div>
      ))}
    </div>
  );

  return (
    <div>
      {/* ── 1. Terms ── */}
      <div className="card" style={{ padding: 16, maxWidth: 760 }}>
        <h2 style={{ fontSize: 16, marginTop: 0 }}>1. Terms</h2>
        <div className="form-grid">
          <label htmlFor="lb-name">Name</label>
          <input id="lb-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} placeholder="e.g. Consolidated Loan Agreement" />

          <label htmlFor="lb-contact">{text.contact}</label>
          <ContactPicker
            companyId={companyId}
            value={contactId}
            onChange={setContactId}
            contactType={side === 'payable' ? 'vendor' : undefined}
            defaultNewType={side === 'payable' ? 'vendor' : undefined}
            placeholder="— optional —"
            id="lb-contact"
          />

          <label htmlFor="lb-principal">Principal</label>
          <input id="lb-principal" inputMode="decimal" value={principal} onChange={(e) => setPrincipal(cleanMoney(e.target.value))} placeholder="0.00" style={{ maxWidth: 180, textAlign: 'right' }} />

          <label htmlFor="lb-rate">Annual rate (%)</label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <input id="lb-rate" inputMode="decimal" value={annualRatePct} onChange={(e) => setAnnualRatePct(cleanMoney(e.target.value))} style={{ width: 110, textAlign: 'right' }} />
            <select value={rateBasis} onChange={(e) => setRateBasis(e.target.value as RateBasis)} aria-label="Rate basis">
              {RATE_BASES.map((b) => <option key={b} value={b}>{RATE_BASIS_LABELS[b]}</option>)}
            </select>
          </div>

          <label htmlFor="lb-freq">Payments</label>
          <select id="lb-freq" value={frequency} onChange={(e) => setFrequency(e.target.value as PaymentFrequency)} style={{ maxWidth: 220 }}>
            {PAYMENT_FREQUENCIES.map((f) => <option key={f} value={f}>{PAYMENT_FREQUENCY_LABELS[f]}</option>)}
          </select>

          <label htmlFor="lb-amort">Amortized over</label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input id="lb-amort" inputMode="numeric" value={amortizationPeriods} onChange={(e) => setAmortizationPeriods(e.target.value.replace(/[^0-9]/g, ''))} style={{ width: 90, textAlign: 'right' }} />
            <span className="muted" style={{ fontSize: 13 }}>payments ≈</span>
            <input inputMode="decimal" value={amortYears || ''} onChange={(e) => setAmortYears(e.target.value)} style={{ width: 70, textAlign: 'right' }} aria-label="Amortization years" />
            <span className="muted" style={{ fontSize: 13 }}>years — sizes the payment</span>
          </div>

          <label htmlFor="lb-count">Number of payments</label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input id="lb-count" inputMode="numeric" value={numberOfPayments} onChange={(e) => setNumberOfPayments(e.target.value.replace(/[^0-9]/g, ''))} placeholder={String(amortPeriodsNum || '')} style={{ width: 90, textAlign: 'right' }} />
            <span className="muted" style={{ fontSize: 13 }}>
              {paymentsNum > 0 ? `≈ ${paymentsYears} years · ` : ''}
              {paymentsNum < amortPeriodsNum ? 'fewer than the amortization → balloon at maturity' : 'fully amortizing, no balloon'}
            </span>
          </div>

          <label htmlFor="lb-pmt">Payment amount</label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input id="lb-pmt" inputMode="decimal" value={paymentOverride} onChange={(e) => setPaymentOverride(cleanMoney(e.target.value))} placeholder={summary ? summary.paymentAmount : 'computed'} style={{ width: 140, textAlign: 'right' }} />
            <span className="muted" style={{ fontSize: 13 }}>leave blank to use the level payment</span>
          </div>

          <label htmlFor="lb-orig">Origination date</label>
          <input id="lb-orig" type="date" value={originationDate} onChange={(e) => setOriginationDate(e.target.value)} style={{ maxWidth: 200 }} />

          <label htmlFor="lb-first">First payment date</label>
          <input id="lb-first" type="date" value={firstPaymentDate} onChange={(e) => { setFirstTouched(true); setFirstPaymentDate(e.target.value); }} style={{ maxWidth: 200 }} />

          <label>Due dates</label>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
            <input
              type="checkbox"
              checked={businessDayRule === 'next_business_day'}
              onChange={(e) => setBusinessDayRule(e.target.checked ? BUSINESS_DAY_RULES[1] : BUSINESS_DAY_RULES[0])}
            />
            Move a due date that lands on a weekend or bank holiday to the next business day
          </label>
        </div>

        <details style={{ marginTop: 8 }}>
          <summary style={{ cursor: 'pointer', fontSize: 13 }}>Late charge and default rate (optional)</summary>
          <div className="form-grid" style={{ marginTop: 8 }}>
            <label htmlFor="lb-late">Late charge (%)</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input id="lb-late" inputMode="decimal" value={lateChargePct} onChange={(e) => setLateChargePct(cleanMoney(e.target.value))} placeholder="e.g. 5" style={{ width: 90, textAlign: 'right' }} />
              <span className="muted" style={{ fontSize: 13 }}>of the installment, after</span>
              <input inputMode="numeric" value={graceDays} onChange={(e) => setGraceDays(e.target.value.replace(/[^0-9]/g, ''))} placeholder="10" style={{ width: 70, textAlign: 'right' }} aria-label="Grace days" />
              <span className="muted" style={{ fontSize: 13 }}>days</span>
            </div>
            <label htmlFor="lb-default">Default rate spread (%)</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input id="lb-default" inputMode="decimal" value={defaultSpread} onChange={(e) => setDefaultSpread(cleanMoney(e.target.value))} placeholder="e.g. 3" style={{ width: 90, textAlign: 'right' }} />
              <span className="muted" style={{ fontSize: 13 }}>added to the rate while in default — shown, never posted automatically</span>
            </div>
          </div>
        </details>
      </div>

      {/* ── Preview ── */}
      {summary && (
        <div className="card" style={{ padding: 16, maxWidth: 760, marginTop: 16 }}>
          <h2 style={{ fontSize: 16, marginTop: 0 }}>Projected schedule</h2>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 12 }}>
            <Tile label={`${PAYMENT_FREQUENCY_LABELS[frequency]} payment`} value={formatCurrency(summary.paymentAmount)} strong />
            <Tile label="Payments" value={String(summary.numberOfPayments)} />
            <Tile label="Maturity" value={formatAccountingDate(summary.maturityDate)} />
            <Tile label="Balloon at maturity" value={formatCurrency(summary.balloon)} strong={Number(summary.balloon) > 0} />
            <Tile label="Total interest" value={formatCurrency(summary.totalInterest)} />
            <Tile label="Total paid" value={formatCurrency(summary.totalPaid)} />
          </div>
          <p className="muted" style={{ fontSize: 12, margin: '0 0 8px' }}>
            Periodic rate {summary.periodicRatePct}% per {frequency === 'weekly' ? 'week' : frequency === 'biweekly' ? 'two weeks' : frequency === 'monthly' ? 'month' : 'quarter'}, derived from the annual rate at full precision.
          </p>
          <div style={{ maxHeight: 360, overflowY: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Due</th>
                  <th style={{ textAlign: 'right' }}>Payment</th>
                  <th style={{ textAlign: 'right' }}>Principal</th>
                  <th style={{ textAlign: 'right' }}>Interest</th>
                  <th style={{ textAlign: 'right' }}>Balance</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.filter((r) => r.kind === 'scheduled').map((r) => (
                  <tr key={r.periodNo}>
                    <td>{r.periodNo}</td>
                    <td>{formatAccountingDate(r.dueDate)}</td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(r.payment)}</td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(r.principal)}</td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(r.interest)}</td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(r.balance)}</td>
                  </tr>
                ))}
                {!showAllRows && schedule.length > visibleRows.length && (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center' }}>
                      <button type="button" className="btn-secondary" onClick={() => setShowAllRows(true)}>
                        Show all {schedule.filter((r) => r.kind === 'scheduled').length} payments
                      </button>
                    </td>
                  </tr>
                )}
                {balloonRow && (
                  <tr style={{ fontWeight: 600 }}>
                    <td>Balloon</td>
                    <td>{formatAccountingDate(balloonRow.dueDate)}</td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(balloonRow.payment)}</td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(balloonRow.principal)}</td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency('0')}</td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency('0')}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <p className="muted" style={{ fontSize: 12, marginTop: 8 }}>
            Projection only — nothing is posted until you save the loan.
          </p>
        </div>
      )}

      {/* ── 2. Funding ── */}
      <div className="card" style={{ padding: 16, maxWidth: 760, marginTop: 16 }}>
        <h2 style={{ fontSize: 16, marginTop: 0 }}>2. Funding</h2>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, marginBottom: 6 }}>
          <input type="radio" name="lb-funding" checked={fundingMode === 'existing'} onChange={() => setFundingMode('existing')} />
          {text.existingLabel}
        </label>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
          <input type="radio" name="lb-funding" checked={fundingMode === 'post'} onChange={() => setFundingMode('post')} />
          {text.postLabel}
        </label>

        {fundingMode === 'post' && (
          <div>
            {fundingTable(text.consolidateTitle, text.consolidateHint, consolidate, setConsolidate, isPrincipalSelectable)}
            {fundingTable(text.advancesTitle, text.advancesHint, advances, setAdvances, isBankSelectable)}
            <div style={{ marginTop: 12, fontSize: 13 }}>
              Sources total <strong>{formatCurrency(String(fundingTotal))}</strong> against principal <strong>{formatCurrency(String(principalNum))}</strong>
              {Math.abs(fundingDiff) >= 0.005 ? (
                <span className="field-error" style={{ marginLeft: 8 }}>off by {formatCurrency(String(Math.abs(fundingDiff)))}</span>
              ) : (
                <span className="tag ok" style={{ marginLeft: 8 }}>balances</span>
              )}
            </div>
            {fundingOk && principalAccountId && (
              <div style={{ marginTop: 10 }}>
                <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>Origination entry on {formatAccountingDate(originationDate)}</div>
                <table className="table">
                  <thead>
                    <tr><th>Account</th><th style={{ textAlign: 'right' }}>Debit</th><th style={{ textAlign: 'right' }}>Credit</th></tr>
                  </thead>
                  <tbody>
                    {side === 'receivable' && (
                      <tr><td>{acctLabel(principalAccountId)}</td><td style={{ textAlign: 'right' }}>{formatCurrency(String(principalNum))}</td><td /></tr>
                    )}
                    {[...consolidate, ...advances].map((r) => (
                      <tr key={r.key}>
                        <td>{acctLabel(r.accountId)}</td>
                        <td style={{ textAlign: 'right' }}>{side === 'payable' ? formatCurrency(money(r.amount)) : ''}</td>
                        <td style={{ textAlign: 'right' }}>{side === 'receivable' ? formatCurrency(money(r.amount)) : ''}</td>
                      </tr>
                    ))}
                    {side === 'payable' && (
                      <tr><td>{acctLabel(principalAccountId)}</td><td /><td style={{ textAlign: 'right' }}>{formatCurrency(String(principalNum))}</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── 3. Accounts & posting ── */}
      <div className="card" style={{ padding: 16, maxWidth: 760, marginTop: 16 }}>
        <h2 style={{ fontSize: 16, marginTop: 0 }}>3. Accounts and posting</h2>
        <div className="form-grid">
          <label>{text.principalAccount}</label>
          <div>
            {pick(principalAccountId, setPrincipalAccountId, isPrincipalSelectable)}
            <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>{text.principalHint}</div>
          </div>
          <label>{text.interestAccount}</label>
          {pick(interestAccountId, setInterestAccountId, isInterestSelectable)}
          <label>{text.bankAccount}</label>
          {pick(bankAccountId, setBankAccountId, isBankSelectable, '— bank account —')}
          <label>Scheduled payments</label>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
            <input type="checkbox" checked={autoPost} onChange={(e) => setAutoPost(e.target.checked)} />
            Post each installment automatically on its due date (the bank feed then matches the posted entry)
          </label>
        </div>

        {errorMessage && <div className="field-error" style={{ marginTop: 8 }}>{errorMessage}</div>}
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button className="btn-primary" onClick={submit} disabled={!valid || isPending}>
            {isPending ? 'Saving…' : 'Save loan'}
          </button>
          <a href={cancelHref} className="btn-secondary" style={{ textDecoration: 'none' }}>Cancel</a>
        </div>
      </div>
    </div>
  );
}

function Tile({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div>
      <div className="muted" style={{ fontSize: 12 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: strong ? 700 : 400 }}>{value}</div>
    </div>
  );
}
